import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { sectorData, getSectorEmoji } from '../sectorData';

const SectorSelector = ({ selectedSectors = [], onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const sectorOptions = Object.keys(sectorData).map(name => ({
    name,
    emoji: getSectorEmoji(name)
  }));

  const filteredOptions = sectorOptions.filter(sector =>
    !selectedSectors.includes(sector.name) && 
    sector.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (sectorName) => {
    const newSelection = selectedSectors.includes(sectorName)
      ? selectedSectors.filter(s => s !== sectorName)
      : [...selectedSectors, sectorName];
    onChange(newSelection);
    setSearchTerm('');
    inputRef.current?.focus();
  };

  const handleRemove = (sectorToRemove, event) => {
    event.stopPropagation();
    onChange(selectedSectors.filter(sector => sector !== sectorToRemove));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Sectors
      </label>
      <div
        className="flex flex-wrap items-center gap-2 p-2 border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500"
        onClick={() => inputRef.current?.focus()}
      >
        {selectedSectors && selectedSectors.map(sector => (
          <span
            key={sector}
            className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-1 py-0 rounded-md text-sm"
          >
            {getSectorEmoji(sector)} {sector}
            <button
              type="button"
              onClick={(e) => handleRemove(sector, e)}
              className="hover:text-blue-600"
            >
              <X size={14} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={selectedSectors.length === 0 ? "Search sectors..." : ""}
          className="flex-1 outline-none min-w-[120px] text-sm"
        />
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-gray-500">No results found</div>
            ) : (
              filteredOptions.map(({ name, emoji }) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => handleSelect(name)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
                >
                  <span>{emoji}</span>
                  <span>{name}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SectorSelector;